/* ============================================================
   AM2T — Cart Pack Engine
   Auto-adds Triumph Bottle Orange when cart contains
   (1+ pack-top) + (1+ pack-bottom) + (1+ pack-socks).
   Auto-removes when pack is broken.
   Listens to 'cart:update'. Debounces 500ms to avoid race conditions.
   Reads variant IDs from window.AM2T.packEligibleIds (set by theme.liquid).

   PACK DISCOUNT: when the pack is complete, links to /checkout get
   ?discount=PACK_DISCOUNT_CODE appended so the checkout charges the
   pack price (129 EUR instead of 131 EUR). The discount code must
   exist in Shopify Admin -> Discounts and match PACK_DISCOUNT_CODE.
   ============================================================ */

(() => {
  'use strict';

  const DEBOUNCE_MS = 500;

  // Must match the discount code created in Shopify Admin (Discounts).
  // Leave as '' to disable the automatic application.
  const PACK_DISCOUNT_CODE = 'COMPETITIONPACK';

  const eligible = () => (window.AM2T && window.AM2T.packEligibleIds) || null;

  class CartPackEngine {
    constructor() {
      this.syncing = false;
      this.timer = null;
      this.packComplete = false;
      this.onCartUpdate = this.onCartUpdate.bind(this);
      this.scheduleSync = this.scheduleSync.bind(this);
      this.onCheckoutClick = this.onCheckoutClick.bind(this);

      document.addEventListener('cart:update', this.onCartUpdate);

      // Capture phase: runs before the click navigates, and survives the
      // cart drawer being re-rendered (listener lives on document).
      document.addEventListener('click', this.onCheckoutClick, true);

      // Initial check (cart could already contain a pack from a previous session).
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.scheduleSync, { once: true });
      } else {
        this.scheduleSync();
      }
    }

    onCartUpdate(e) {
      if (e.detail?.source === 'cart-pack-engine') return; // ignore self
      this.scheduleSync();
    }

    // Adds / removes the pack discount code on checkout links.
    onCheckoutClick(e) {
      if (!PACK_DISCOUNT_CODE) return;
      const link = e.target.closest ? e.target.closest('a[href^="/checkout"]') : null;
      if (!link) return;

      try {
        const url = new URL(link.getAttribute('href'), window.location.origin);
        if (this.packComplete) {
          url.searchParams.set('discount', PACK_DISCOUNT_CODE);
        } else if (url.searchParams.get('discount') === PACK_DISCOUNT_CODE) {
          url.searchParams.delete('discount');
        }
        link.setAttribute('href', url.pathname + url.search);
      } catch (err) {
        console.warn('[cart-pack-engine] could not update checkout link:', err);
      }
    }

    scheduleSync() {
      if (this.timer) clearTimeout(this.timer);
      this.timer = setTimeout(() => this.checkAndSync(), DEBOUNCE_MS);
    }

    async checkAndSync() {
      if (this.syncing) return;
      const ids = eligible();
      if (!ids) {
        console.warn('[cart-pack-engine] window.AM2T.packEligibleIds not available');
        return;
      }

      this.syncing = true;
      try {
        const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!cartRes.ok) throw new Error(`HTTP ${cartRes.status}`);
        const cart = await cartRes.json();

        const items = cart.items || [];
        const orangeId = ids.orangeBottleVariantId;

        const hasTop = items.some((i) => ids.tops.includes(i.variant_id));
        const hasBottom = items.some((i) => ids.bottoms.includes(i.variant_id));
        const hasSocks = items.some((i) => ids.socks.includes(i.variant_id));
        const bottleItem = orangeId ? items.find((i) => i.variant_id === orangeId) : null;

        const packComplete = hasTop && hasBottom && hasSocks;
        this.packComplete = packComplete;

        // === GREY BOTTLE — evaluate BEFORE orange so a pack-complete
        // transition removes grey before orange tries to add itself.
        // Rule: never grey AND orange simultaneously. Pack-complete wins.
        const greyId = ids.greyBottleVariantId;
        const greyEnabled = ids.greyBottleEnabled;
        const greyThresholdCents = (ids.greyBottleThreshold || 90) * 100;
        const greyItem = greyId ? items.find((i) => i.variant_id === greyId) : null;
        const subtotal = cart.items_subtotal_price || 0;

        if (greyEnabled && greyId) {
          if (packComplete && greyItem) {
            await this.removeGreyBottle(greyItem.key);
          } else if (!packComplete && subtotal >= greyThresholdCents && !greyItem) {
            await this.addGreyBottle();
          } else if (!packComplete && subtotal < greyThresholdCents && greyItem) {
            await this.removeGreyBottle(greyItem.key);
          }
        }

        if (packComplete && !bottleItem) {
          if (!orangeId) {
            console.warn(
              '[cart-pack-engine] cannot add bottle: orangeBottleVariantId is 0. ' +
              'Check that "triumph-bottle-orange" product exists AND is published to the Online Store sales channel ' +
              '(otherwise Shopify excludes it from collections.all and the storefront cart API rejects it).'
            );
            return;
          }
          await this.addOrangeBottle();
        } else if (!packComplete && bottleItem) {
          await this.removeOrangeBottle(bottleItem.key);
        }
      } catch (err) {
        console.warn('[cart-pack-engine] sync failed:', err);
      } finally {
        this.syncing = false;
      }
    }

    async addOrangeBottle() {
      const ids = eligible();
      if (!ids?.orangeBottleVariantId) {
        console.warn('[cart-pack-engine] addOrangeBottle aborted: no orangeBottleVariantId');
        return;
      }

      try {
        const addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{
              id: ids.orangeBottleVariantId,
              quantity: 1,
              properties: { _gift: 'true', _free: 'true' }
            }]
          })
        });
        if (!addRes.ok) {
          const txt = await addRes.text();
          throw new Error(`HTTP ${addRes.status}: ${txt}`);
        }

        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: { cart, source: 'cart-pack-engine' }
        }));
      } catch (err) {
        console.warn('[cart-pack-engine] add bottle failed:', err);
      }
    }

    async removeOrangeBottle(key) {
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 })
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: { cart, source: 'cart-pack-engine' }
        }));
      } catch (err) {
        console.warn('[cart-pack-engine] remove bottle failed:', err);
      }
    }

    async addGreyBottle() {
      const ids = eligible();
      if (!ids?.greyBottleVariantId) {
        console.warn('[cart-pack-engine] addGreyBottle aborted: no greyBottleVariantId');
        return;
      }

      try {
        const addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{
              id: ids.greyBottleVariantId,
              quantity: 1,
              properties: { _gift: 'true', _free: 'true' }
            }]
          })
        });
        if (!addRes.ok) {
          const txt = await addRes.text();
          throw new Error(`HTTP ${addRes.status}: ${txt}`);
        }

        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: { cart, source: 'cart-pack-engine' }
        }));
      } catch (err) {
        console.warn('[cart-pack-engine] add grey bottle failed:', err);
      }
    }

    async removeGreyBottle(key) {
      if (!key) return;
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 })
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: { cart, source: 'cart-pack-engine' }
        }));
      } catch (err) {
        console.warn('[cart-pack-engine] remove grey bottle failed:', err);
      }
    }
  }

  new CartPackEngine();
})();